'use client';

import { useState, useCallback, useEffect } from 'react';
import Toast, { ToastProps } from './index';

export interface ToastItem {
  id: string;
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  timestamp: number;
}

class ToastService {
  private toasts: ToastItem[] = [];
  private listeners: ((toasts: ToastItem[]) => void)[] = [];
  private maxToasts = 5; // 最多显示5个Toast

  subscribe(listener: (toasts: ToastItem[]) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notify() {
    this.listeners.forEach(listener => listener([...this.toasts]));
  }

  show(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info', duration = 3000) {
    const id = Math.random().toString(36).substr(2, 9);
    const toast: ToastItem = {
      id,
      message,
      type,
      duration,
      timestamp: Date.now(),
    };

    // 如果超过最大数量，移除最旧的
    if (this.toasts.length >= this.maxToasts) {
      this.toasts.shift();
    }

    this.toasts.push(toast);
    this.notify();

    // 自动移除
    setTimeout(() => {
      this.remove(id);
    }, duration);
  }

  remove(id: string) {
    this.toasts = this.toasts.filter(toast => toast.id !== id);
    this.notify();
  }

  clear() {
    this.toasts = [];
    this.notify();
  }

  // 便捷方法
  success(message: string, duration = 3000) {
    this.show(message, 'success', duration);
  }

  error(message: string, duration = 5000) {
    this.show(message, 'error', duration);
  }

  warning(message: string, duration = 4000) {
    this.show(message, 'warning', duration);
  }

  info(message: string, duration = 3000) {
    this.show(message, 'info', duration);
  }
}

export const toastService = new ToastService();

export const ToastManager = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    const unsubscribe = toastService.subscribe(setToasts);
    return unsubscribe;
  }, []);

  const removeToast = useCallback((id: string) => {
    toastService.remove(id);
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-5000 w-full">
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          className="absolute w-full"
          style={{
            bottom: `${window.innerHeight * 0.25 + index * 80}px`, // 屏幕下方3/4处，垂直堆叠
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '0 16px', // 添加左右内边距
          }}
        >
          <Toast
            {...toast}
            onClose={removeToast}
          />
        </div>
      ))}
    </div>
  );
};

// 全局方法
if (typeof window !== 'undefined') {
  (window as any).toast = toastService;
}

export default ToastManager;
